"use client"

import { useState, useEffect } from "react"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { Menu, X } from "lucide-react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext";
import { VBotLogo } from "./ui/vbot-logo";
import { useSignedUrlRetry } from "../hooks/useSignedUrlRetry";
import { toAbsoluteAvatarUrl, fetchAuthMeAvatarUrl } from "../utils/avatarUrl";

// Avatar URLs are short-lived pre-signed S3 URLs: on load failure refetch /auth/me once for a fresh one.
function HeaderAvatar({ avatarUrl, displayName, className }) {
  const { src, onLoadingStatusChange } = useSignedUrlRetry(avatarUrl, fetchAuthMeAvatarUrl);
  return (
    <Avatar className={className}>
      <AvatarImage src={src} alt={displayName} onLoadingStatusChange={onLoadingStatusChange} />
      <AvatarFallback className="bg-blue-600 text-white font-medium">
        {(displayName || "").slice(0, 2).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { currentUser, loading } = useAuth();
  const location = useLocation()
  const navigate = useNavigate()
  const pathname = location.pathname

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10)
    window.scrollTo(0, 0);
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "auto"
    return () => { document.body.style.overflow = "auto" }
  }, [isMobileMenuOpen])

  const handleNavClick = (item) => {
    if (item.path) {
      navigate(item.path)
    } else if (item.id === "pricing") {
      navigate("/pricing")
    } else if (pathname !== "/") {
      navigate(`/#${item.id}`)
    } else {
      const element = document.getElementById(item.id)
      if (element) element.scrollIntoView({ behavior: "smooth" })
    }
    setIsMobileMenuOpen(false)
  }

  const handleHomeClick = () => {
    if (pathname !== "/") {
      navigate("/")
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
    setIsMobileMenuOpen(false)
  }

  const handleProfileClick = () => {
    navigate("/scribe/settings")
    setIsMobileMenuOpen(false)
  }

  const isAuthenticated = Boolean(currentUser);
  const showMarketingNav = !isAuthenticated;

  const profile = currentUser?.profile || {}
  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ")
  const fallbackName = currentUser?.email ? currentUser.email.split("@")[0] : ""
  const displayName = fullName || fallbackName || "Your account"
  const avatarRaw = profile.profileImagePath || profile.profileimage || ""
  const avatarUrl = toAbsoluteAvatarUrl(avatarRaw)

  const navItems = [
    { id: "features", label: "Features" },
    { path: "/how-its-work", label: "How it Works" },
    { id: "benefits", label: "Benefits" },
    { id: "pricing", label: "Pricing" },
    { id: "demo", label: "Demo" },
  ]

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 shadow-lg border-b" : "bg-white/80 border-b"
      }`}
    >
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo — same mark as homepage */}
        <div className="flex items-center cursor-pointer flex-shrink-0" onClick={handleHomeClick}>
          <VBotLogo
            size="md"
            withWordmark
            className="text-lg sm:text-xl"
          />
          {pathname !== "/payment" && (
            <Badge variant="secondary" className="ml-2 animate-pulse text-xs">v1.1</Badge>
          )}
        </div>

        {/* Desktop Nav */}
        {showMarketingNav && (
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <span
                key={item.label}
                onClick={() => handleNavClick(item)}
                className="text-gray-600 hover:text-blue-600 cursor-pointer relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full"></span>
              </span>
            ))}
          </nav>
        )}

        {/* Desktop Right */}
        <div className="hidden md:flex items-center space-x-3">
          {!loading && currentUser ? (
            <div onClick={handleProfileClick} className="flex items-center space-x-6 cursor-pointer bg-gradient-to-r from-blue-100 to-indigo-100 px-4 py-2 rounded-full hover:shadow-md transition">
              <HeaderAvatar
                avatarUrl={avatarUrl}
                displayName={displayName}
                className="h-14 w-14 border border-blue-200 bg-white"
              />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-900">{displayName}</span>
                {profile.roleTitle && <span className="text-xs text-gray-600">{profile.roleTitle}</span>}
              </div>
            </div>
          ) : (
            <>
              <Link to="/signin"><Button variant="outline" size="sm">Sign In</Button></Link>
              <Link to="/signup"><Button size="sm" className="bg-gradient-to-r from-blue-600 to-indigo-600 text-slate-50">Get Started</Button></Link>
            </>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-gray-100"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Nav - FIXED: larger max height and scrollable */}
      <div className={`md:hidden transition-all duration-300 overflow-hidden ${isMobileMenuOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="bg-white border-t px-4 py-4 space-y-4 overflow-y-auto max-h-[80vh]">
          {showMarketingNav &&
            navItems.map((item) => (
              <span
                key={item.label}
                onClick={() => handleNavClick(item)}
                className="block text-gray-600 hover:text-blue-600 cursor-pointer py-2"
              >
                {item.label}
              </span>
            ))}
          <div className="flex flex-col space-y-3 pt-4 border-t">
            {!loading && currentUser ? (
              <div onClick={handleProfileClick} className="flex items-center space-x-3 cursor-pointer bg-gradient-to-r from-blue-100 to-indigo-100 p-3 rounded-lg">
                <HeaderAvatar
                  avatarUrl={avatarUrl}
                  displayName={displayName}
                  className="h-10 w-10 border border-blue-200 bg-white"
                />
                <div className="flex flex-col text-left">
                  <span className="text-sm font-semibold text-gray-900">{displayName}</span>
                  {profile.roleTitle && <span className="text-xs text-gray-600">{profile.roleTitle}</span>}
                </div>
              </div>
            ) : (
              <>
                <Link to="/signin" className="w-full">
                  <Button variant="outline" size="sm" className="w-full">Sign In</Button>
                </Link>
                <Link to="/signup" className="w-full">
                  <Button size="sm" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600">Get Started</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}